import { calculateDistanceEngines } from 'constants/maps';
import { ApplicationState } from 'redux/reducers';
import routes from 'Routes/routesConfig';
import {
  Configuration,
  ConfigurationKeys,
  DecisionTreeNode,
  GtmPerEnv,
  IdentificationPage,
  MenuLink,
  Passengers,
  PoliciesData,
  Reason,
  RegexData,
  StepOrderType,
  SummaryItemData,
  SynergyCaseStatusStepType,
  Theme,
  TimeSlotData,
  TypedMap,
  HeaderProjects,
  ShowExpectationManagementEnum,
  ContextQuestion,
  PoTools,
  SafetyInstructions,
} from 'ts/types';
import { ComputableContractsQuestions } from 'ts/types/coverage-questions.types';

import { caseStatusSteps } from 'constants/case-status';
import { getStepIndex } from 'utils/case-status-utils';
import { DisplayPassengersDetailsOptions } from '../../containers/SynergyPassengers/types';
import { caseStatusStateSelector } from './caseStatus';

const configSelector = (state: ApplicationState): Configuration =>
  state.application.configuration;

export const configKeySelector = (state: ApplicationState): ConfigurationKeys =>
  configSelector(state).keys || {};

export const showPoTermsSelector = (state: ApplicationState): boolean =>
  state.application.shouldDisplayPoTerms;

export const showPrismicCheckerSelector = (state: ApplicationState): boolean =>
  state.application.shouldDisplayPrismicChecker;

export const serviceQualifierTreeSelector = (
  state: ApplicationState,
): TypedMap<string> => configSelector(state).service_qualifier_tree || {};

export const computableContractsDefaultProductIdSelector = (
  state: ApplicationState,
): string => configKeySelector(state).computable_contracts_default_product_id;

export const computableContractsStaticQuestionsSelector = (
  state: ApplicationState,
): ComputableContractsQuestions[] => {
  const staticQuestions =
    configSelector(state).computable_contracts_static_questions || [];
  return staticQuestions?.length > 0
    ? staticQuestions.map(obj => ({
        id: obj.id,
        value: obj.type === 'BOOLEAN' ? obj.value === 'true' : obj.value,
        type: obj.type,
        priority: obj.priority ? parseInt(obj.priority.toString(), 10) : 0,
      }))
    : [];
};

export const computableContractsDefaultDynamicQuestionsSelector = (
  state: ApplicationState,
): ComputableContractsQuestions[] => {
  const defaultDynamicQuestions =
    configSelector(state).computable_contracts_default_dynamic_questions || [];
  return defaultDynamicQuestions?.length > 0
    ? defaultDynamicQuestions.map(obj => ({
        id: obj.id,
        value: obj.type === 'BOOLEAN' ? obj.value === 'true' : obj.value,
        type: obj.type,
        priority: obj.priority ? parseInt(obj.priority.toString(), 10) : 0,
      }))
    : [];
};

export const icvcPageSelector = (state: ApplicationState): string =>
  configKeySelector(state).icvc_page;

export const partnerSelector = (state: ApplicationState): string =>
  configKeySelector(state).config_file;

export const partnerNameSelector = (state: ApplicationState): string =>
  configKeySelector(state).partner_name;

export const addressModalMaskPatternSelector = (
  state: ApplicationState,
): string => configKeySelector(state).address_modal_mask_pattern;

export const selectTheme = (state: ApplicationState): Theme =>
  configSelector(state)?.theme;

export const emergencyBannerMessagesSelector = (
  state: ApplicationState,
): TypedMap<string> => configSelector(state)?.emergency_banner_messages || {};

export const selectFavIcon = (state: ApplicationState): string =>
  configKeySelector(state).favicon;

export const selectBrandImage = (state: ApplicationState): string =>
  configKeySelector(state).brand_image;

export const selectGlobalHeaderTitle = (state: ApplicationState): string =>
  configKeySelector(state).global_header_title;

export const selectDTOption = (state: ApplicationState): string =>
  configKeySelector(state).decision_tree_option;

export const selectBrand = (state: ApplicationState): string =>
  configKeySelector(state).brand;

export const selectMetadata = (state: ApplicationState): string | undefined =>
  configKeySelector(state).metas;

export const selectShowPrivacyPolicyModal = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_private_policy_modal;

export const shouldDisplayTitleOnSummaryPageSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_title_on_summary_page;

export const shouldDisplayProblemOnSummaryPageSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_problem_on_summary_page;

export const shouldDisplayProblemTitleOnSummaryPageSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state).should_display_problem_title_on_summary_page;

export const shouldDisplaySummaryDetailsSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_summary_details;

export const isSummaryBlackTitleModeSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).is_summary_black_title_mode;
export const shouldDisplayStepHeaderSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_step_header;

export const shouldHaveStickyHeaderSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_have_sticky_header;

export const selectShowDisplayBrandHeader = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_brand_header;

export const headerToBeDisplayedSelector = (
  state: ApplicationState,
): HeaderProjects => configKeySelector(state).header_to_be_displayed;

export const selectMenuLinks = (state: ApplicationState): MenuLink[] =>
  configSelector(state)?.menu_links || [];

export const selectShowPropertyOccupancy = (state: ApplicationState): boolean =>
  configKeySelector(state).should_display_property_occupancy;

export const selectShowUnreachableCallUsButton = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state).should_display_unreachable_call_us_button;

export const selectPolicies = (state: ApplicationState): PoliciesData =>
  configSelector(state).policies;

export const currentLanguageSelector = (state: ApplicationState): string =>
  state.application.currentLanguage || selectLanguage(state);

export const selectLanguage = (state: ApplicationState): string =>
  configKeySelector(state).default_language;

export const selectLanguages = (state: ApplicationState): string[] =>
  configSelector(state).supported_languages;

export const selectProvider = (state: ApplicationState): string =>
  configKeySelector(state).provider;

export const selectCookiesUrlPolicies = (
  state: ApplicationState,
): string | undefined => selectPolicies(state)?.cookies_policy?.url;

export const selectDateFormat = (state: ApplicationState): string =>
  configKeySelector(state).date_format;

export const selectLongDateFormat = (state: ApplicationState): string =>
  configKeySelector(state).long_date_format;

export const shortDateFormatSelector = (state: ApplicationState): string =>
  configKeySelector(state).short_date_format;

export const shouldVerifyMaxCreatedCasesSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_verify_max_created_cases;

export const maxCreatedCasesAllowedSelector = (
  state: ApplicationState,
): number => configKeySelector(state).max_created_cases_allowed;

export const hasMobilityContinuationSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_have_mobility_continuity;

export const hasMobilityContinuationStaticPageSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state).should_have_mobility_continuation_static_page;

export const shouldDisplayAllServiceActivityDetailsPerStepSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state).should_display_all_service_activity_details_per_step;

export const mobilityContinuationFirstStepSelector = (
  state: ApplicationState,
): string | undefined =>
  configKeySelector(state).mobility_continuity_first_step;

export const mobilityContinuationSupportedServicesSelector = (
  state: ApplicationState,
) => {
  const services =
    configKeySelector(state).mobility_continuation_supported_services.map(
      ({ key, icon, title, should_show_banner }) => ({
        key,
        icon,
        title,
        should_show_banner: should_show_banner === 'true',
      }),
    ) || [];
  return services;
};

export const MCSupportedServiceKeysSelector = (state: ApplicationState) => {
  const services =
    configKeySelector(state).mobility_continuation_supported_services || [];
  return services.map(service => service.key);
};

export const mobilityContinuationServicesContentSelector = (
  state: ApplicationState,
) => {
  const servicesContent =
    configKeySelector(state).mobility_continuation_services_content || [];
  return servicesContent;
};

export const shouldEnableGarageOrientationSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_enable_garage_orientation;

export const garageOrientationOptionsSelector = (state: ApplicationState) => {
  const partnerCountry =
    configKeySelector(state).garage_orientation_partner_country;
  const searchRadius = (configKeySelector(state)
    .garage_orientation_search_radius || [])[0];
  const searchNetworks =
    configKeySelector(state).garage_orientation_search_networks;
  return {
    partner_country: partnerCountry,
    search_radius: searchRadius,
    search_networks: searchNetworks,
  };
};

export const isDestinationFeatureEnabledSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).is_destination_feature_enabled;

export const isMobilityContinuationStepSelector = (
  state: ApplicationState,
): boolean => {
  const mobilityContinuationFirstStep =
    mobilityContinuationFirstStepSelector(state);
  if (!mobilityContinuationFirstStep) {
    return true;
  }
  const steps = caseStatusStepsSelector(state);
  const caseStatusCurrentStepIndex = caseStatusCurrentStepSelector(state);
  const mobilityContinuationFirstStepIndex = getStepIndex(
    steps,
    mobilityContinuationFirstStep as SynergyCaseStatusStepType,
  );
  return caseStatusCurrentStepIndex >= mobilityContinuationFirstStepIndex;
};

export const shouldHideMobilityContinuationButtonOnStatusPageSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state)
    .should_hide_mobility_continuity_button_on_status_page;

export const shouldRedirectToCallbackIfServicesRejectedSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state).should_redirect_to_callback_if_services_rejected;

export const selectPrivatePoliciesUrl = (
  state: ApplicationState,
): string | undefined => selectPolicies(state)?.privacy_policy?.url;

export const selectTermsAndConditionsPoliciesUrl = (
  state: ApplicationState,
): string | undefined =>
  selectPolicies(state)?.terms_and_conditions_policy?.url;

export const availableRescheduleDaysSelector = (
  state: ApplicationState,
): boolean[] => [
  configKeySelector(state).should_enable_sunday,
  configKeySelector(state).should_enable_monday,
  configKeySelector(state).should_enable_tuesday,
  configKeySelector(state).should_enable_wednesday,
  configKeySelector(state).should_enable_thursday,
  configKeySelector(state).should_enable_friday,
  configKeySelector(state).should_enable_saturday,
];

export const showAddCalendarConfigSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_add_calendar;

export const calendarDefaultHoursSelector = (state: ApplicationState): number =>
  configKeySelector(state).calendar_default_hours;

export const availabilityHoursSelector = (
  state: ApplicationState,
): { start_hours: string[]; end_hours: string[] } => ({
  start_hours: configSelector(state).appointment_reschedule_start_hours,
  end_hours: configSelector(state).appointment_reschedule_end_hours,
});

export const timeSlotOptionsSelector = (
  state: ApplicationState,
): TimeSlotData[] => configSelector(state).time_slot_options;

export const showStepsSelector = (state: ApplicationState): boolean =>
  configKeySelector(state).should_display_step_header_steps;

export const showNameSelector = (state: ApplicationState): boolean =>
  configKeySelector(state).should_display_step_header_name;

export const showProgressBarSelector = (state: ApplicationState): boolean =>
  configKeySelector(state).should_display_steps_progress_bar;

export const showProgressBarFinalStepsSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state).should_display_steps_progress_bar_final_steps;

export const showIncidentDescriptionInfoBanner = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state).should_display_incident_description_info_banner;

export const showCaseCreationPageSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_creation_page;

export const showCaseReferenceSelector = (state: ApplicationState): boolean =>
  configKeySelector(state).should_display_case_reference;

export const showCasePageSelector = (state: ApplicationState): boolean =>
  configKeySelector(state).should_display_case_page;

export const showLookingForProviderSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_looking_for_provider;

export const alertTimeoutSelector = (state: ApplicationState): number =>
  configKeySelector(state).alert_timeout;

export const alertSpecialSelector = (state: ApplicationState): boolean =>
  configKeySelector(state).should_display_special_alert;

export const alertBannerSelector = (state: ApplicationState): boolean =>
  configKeySelector(state).should_display_emergency_banner;

export const emergencyNumberMotorwaySelector = (
  state: ApplicationState,
): string => configKeySelector(state).emergency_number_contact_us_motorway;

export const copyrightLabelSelector = (state: ApplicationState): string =>
  configKeySelector(state).copyright_label;

export const contactPagePhoneSelector = (state: ApplicationState): string =>
  configKeySelector(state).contact_phone;

export const showContactPageSelector = (state: ApplicationState): boolean =>
  configKeySelector(state).should_display_contact_page;

export const shouldShowReasonSelector = (state: ApplicationState): boolean =>
  configKeySelector(state).should_display_contact_reasons;

export const reasonsSelector = (state: ApplicationState): Reason[] =>
  configSelector(state).contact_reasons;

export const safetyInstructionsSelector = (
  state: ApplicationState,
): SafetyInstructions =>
  configSelector(state).contact_us_motorway_safety_instructions;

export const shouldInitiateClaritySelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).is_clarity_enabled;

export const contactPhoneSelector = (state: ApplicationState): string =>
  configKeySelector(state).default_dropout_phone;

export const stepOrderSelector = (state: ApplicationState): StepOrderType => {
  const stepOrder = configSelector(state).step_order;
  const shouldSkipPolicy =
    configKeySelector(state).should_skip_policy_if_from_personal_space;
  return state.caseCreation.skipIdentification && stepOrder && shouldSkipPolicy
    ? configSelector(state)
        .step_order.filter(path => path !== '/identification')
        .map(step => {
          if (typeof step !== 'string') {
            return step.filter(path => path !== '/identification');
          }
          return step;
        })
    : stepOrder;
};

export const identificationOptionsSelector = (
  state: ApplicationState,
): IdentificationPage => configSelector(state).identification;

export const summaryConfigSelector = (
  state: ApplicationState,
): SummaryItemData[] => configSelector(state).summary_config;

export const nextStepsCaseCreationSelector =
  (page: keyof Configuration) =>
  (state: ApplicationState): number =>
    configSelector(state)[page] as number;

export const zipCodePatternSelector = (state: ApplicationState): string =>
  configKeySelector(state).zip_code_pattern;

export const decisionTreeSelector =
  (language: string) =>
  (state: ApplicationState): DecisionTreeNode[] =>
    configSelector(state).decisionTree?.[language];

export const decisionTreeFileNameSelector =
  (language: string) =>
  (state: ApplicationState): string =>
    configSelector(state).decision_tree?.[language];

export const decisionTreeTextToHideSelector = (
  state: ApplicationState,
): string[] => configSelector(state).decision_tree_text_to_hide;

export const maxNumberOfPicturesSelector = (state: ApplicationState): number =>
  configKeySelector(state).maximum_number_of_pictures;

export const backgroundHomeUrlSelector = (state: ApplicationState): string =>
  configKeySelector(state).background_home_url;

export const showLegalDisclaimerSelector = (state: ApplicationState): boolean =>
  configKeySelector(state).should_display_legal_disclaimer;

export const oneTrustSelector = (state: ApplicationState): TypedMap<string> =>
  configSelector(state).one_trust_env;

export const dataDomainScriptIdSelector = (state: ApplicationState): string =>
  oneTrustSelector(state) &&
  oneTrustSelector(state)[
    process.env.REACT_APP_environment?.toLowerCase() || 0
  ];

export const showIntroSelector = (state: ApplicationState): boolean =>
  configKeySelector(state).should_display_intro_page;
export const showSurveysSelector = (state: ApplicationState): boolean =>
  configKeySelector(state).is_qualtrics_enabled;

export const siteInterceptSurveysSelector = (
  state: ApplicationState,
): TypedMap<string> => configSelector(state).survey_per_page;
export const optimizeIDSelector = (state: ApplicationState): string =>
  configKeySelector(state).google_optimize_container_id;
export const optimizeExperimentsSelector = (state: ApplicationState): any =>
  configSelector(state).google_optimize_experiments_list;
export const shouldShowContinueModalSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_pictures_continue_modal;
export const enablePicturesSelector = (state: ApplicationState): boolean =>
  configKeySelector(state).should_enable_pictures;
export const notificationsModalIntervalTimeSelector = (
  state: ApplicationState,
): number => configKeySelector(state).notifications_modal_interval_time;

export const shouldShowEmailSelector = (state: ApplicationState): boolean =>
  configKeySelector(state).should_display_email;
export const regexSelectors = (
  state: ApplicationState,
): { [_: string]: RegexData } => configSelector(state)?.regex;

export const buttonBehaviourIfSuccessSelector = (
  state: ApplicationState,
): string => configKeySelector(state).button_behaviour_if_success;
export const buttonBehaviourIfExcludedSelector = (
  state: ApplicationState,
): string => configKeySelector(state).button_behaviour_if_excluded;
export const buttonBehaviourIfDropOffSelector = (
  state: ApplicationState,
): string => configKeySelector(state).button_behaviour_if_dropoff;
export const buttonBehaviourIfErrorSelector = (
  state: ApplicationState,
): string => configKeySelector(state).button_behaviour_if_error;
export const customStepsSelector = (state: ApplicationState) =>
  configSelector(state).custom_steps;
export const shouldDisplayRequestNotificationsPopupSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state).should_display_request_notifications_popup;
export const notificationAutoRefreshTimeSecSelector = (
  state: ApplicationState,
): number => configKeySelector(state).notification_auto_refresh_time_sec;
export const numberOfOccupancyDaysSelector = (
  state: ApplicationState,
): number => configKeySelector(state).occupancy_days;
export const caseCreationMaxRetriesSelector = (
  state: ApplicationState,
): string => configKeySelector(state).case_creation_max_retries;
export const missionDescriptionSelector = (state: ApplicationState): string =>
  configKeySelector(state).mission_description;
export const isMissionIdentifierUpperCaseSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).is_mission_identifier_upper_case;
export const shouldDisplayMissionHistoryLinkSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_mission_history_link;
export const shouldDisplayFAQButtonSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_faq_button;
export const maximumMbSizeOfPicturesSelector = (
  state: ApplicationState,
): number => configKeySelector(state).maximum_mb_size_of_picture;
export const gtmEnvSelector = (state: ApplicationState): GtmPerEnv =>
  configSelector(state).gtm_env;
export const gtmIdSelector = (state: ApplicationState): string =>
  configKeySelector(state).gtm_container_id;

export const gtmV2Selector = (state: ApplicationState): boolean =>
  !!configKeySelector(state).gtm_v2;

export const businessCategorySelector = (state: ApplicationState): string =>
  configKeySelector(state).business_category;

export const pagesWithoutHeaderSelector = (state: ApplicationState): string[] =>
  configSelector(state).pages_without_header;

export const pagesWithoutFooterSelector = (state: ApplicationState): string[] =>
  configSelector(state).pages_without_footer;

export const shouldDisplayRepudiationCallbackSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_repudiation_callback;

export const shouldDisplayConfirmationButton = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_confirmation_button;

export const shouldDisplayIncidentLocationSubtitleSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state).should_display_incident_location_subtitle;

export const shouldDisplayCallbackButton = (state: ApplicationState): boolean =>
  configKeySelector(state)
    .should_display_follow_my_case_button_in_callback_page;

export const selectLandingImage = (state: ApplicationState): string =>
  configKeySelector(state).landing_image;

export const selectTenantId = (state: ApplicationState): string =>
  configKeySelector(state).tenant_id;

export const selectMoveVersion = (state: ApplicationState): string =>
  configKeySelector(state).move_version;

export const selectPartnerReference = (state: ApplicationState): number =>
  configKeySelector(state).partner_reference;

export const selectParser = (state: ApplicationState) =>
  configKeySelector(state).parser;

export const contactUsImage = (state: ApplicationState): string =>
  configKeySelector(state).contact_us_image;

export const numberOfFAQCaseOpeningQuestionsSelector = (
  state: ApplicationState,
): number => configKeySelector(state).number_of_faq_case_opening_questions;

export const numberOfFAQQuestionsSelector = (state: ApplicationState): number =>
  configKeySelector(state).number_of_faq_questions;

export const numberOfHelpfulTipsCaseOpeningSelector = (
  state: ApplicationState,
): number => configKeySelector(state).number_of_case_opening_helpful_tips;

export const numberOfHelpfulTipsSelector = (state: ApplicationState): number =>
  configKeySelector(state).number_of_helpful_tips;

export const selectShouldDisplayDecisionTreeIcons = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_decision_tree_icons;

export const selectShouldHighlightLetterInDecisionTree = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_highlight_letter_in_decision_tree;

export const selectShouldShowDecisionTreeOptionsNotSelectedInLightGreyColor = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state)
    .should_show_decision_tree_options_not_selected_in_light_grey_color;

export const shouldAppointmentInteractWithCmsSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_appointment_interact_with_cms;

export const shouldDisplayLandingPageWithPlateNumberSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state).should_display_landing_page_with_plate_number;

export const shouldDisplayProviderDetailsSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_provider_details_be_expanded;

export const eventsWithProviderDetailsSelector = (
  state: ApplicationState,
): string[] => configSelector(state).events_with_provider_details;

export const selectPassengerAdultOptions = (
  state: ApplicationState,
): Passengers[] => {
  const options = configSelector(state).passenger_adult_options;
  return options?.length > 0
    ? options.map(opt => ({ label: opt.label, value: +opt.value }))
    : [];
};

export const selectPassengerChildrenOptions = (
  state: ApplicationState,
): Passengers[] => {
  const options = configSelector(state).passenger_child_options;
  return options?.length > 0
    ? options.map(opt => ({ label: opt.label, value: +opt.value }))
    : [];
};

export const selectDisplayPassengerChildren = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_passenger_child_options;

export const selectDisplayPassengerDetails = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_passenger_details;

export const selectPassengerDetailsOptions = (
  state: ApplicationState,
): DisplayPassengersDetailsOptions => {
  const displayBaby =
    configKeySelector(state).should_display_passenger_details_baby;
  const displayDisability =
    configKeySelector(state).should_display_passenger_details_disability;
  const displayPet =
    configKeySelector(state).should_display_passenger_details_pet;
  const displayPregnant =
    configKeySelector(state).should_display_passenger_details_pregnant;

  return {
    displayBaby,
    displayDisability,
    displayPet,
    displayPregnant,
  };
};

export const showMaintenanceDropOffOnCaseOpeningSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state).should_display_maintenance_dropoff_on_case_opening;

export const showMaintenanceDropOffOnFollowUpSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state).should_display_maintenance_dropoff_on_follow_up;

export const showMaintenanceDropOffContactUsButtonSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state).should_show_maintenance_dropoff_contact_us_button;

export const shouldSkipGetPlateSelector = (state: ApplicationState): boolean =>
  configKeySelector(state).should_skip_get_plate;

export const maintenanceDropOffImageSelector = (
  state: ApplicationState,
): string => configKeySelector(state).maintenance_dropoff_image;

export const shouldDisplayUnavailableBannerSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_display_unavailable_banner;

export const unavailablePeriodCronSelector = (
  state: ApplicationState,
): string => configKeySelector(state).unavailable_period_cron;

export const shouldHideLocationDetailsSelector = (
  state: ApplicationState,
): boolean => configKeySelector(state).should_hide_location_details_in_location;

export const showMotorwayQuestionInLocationDetailsSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state).should_show_at_motorway_question_in_location_details;

export const showHeightRestrictionQuestionInLocationDetailsSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state)
    .should_show_height_restriction_question_in_location_details;

export const showTextareaIfInMotorwayInLocationDetailsSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state)
    .should_show_textarea_if_in_motorway_in_location_details;

export const showContactUsMotorwaySelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state)
    .should_show_contact_us_if_is_in_motorway_in_location_details;

export const showExpectationManagementSelector = (
  state: ApplicationState,
): ShowExpectationManagementEnum =>
  configKeySelector(state)?.show_expectation_management ||
  ShowExpectationManagementEnum.NEVER;

export const supportedPhoneCountryCodesSelector = (
  state: ApplicationState,
): string[] | undefined => {
  const supportedCountryCodes =
    configSelector(state)?.supported_phone_country_codes;
  return supportedCountryCodes?.length
    ? supportedCountryCodes.map(({ country_code }) => country_code)
    : undefined;
};

export const defaultPhoneCountryCodeSelector = (state: ApplicationState) =>
  configKeySelector(state).default_phone_country_code;

export const enableCaseStatusStepsSelector = (state: ApplicationState) =>
  configKeySelector(state).steps_in_follow_my_case;

export const mapDefaultLocationSelector = (state: ApplicationState) => {
  return {
    address: {},
    coordinates: configSelector(state).map_default_location,
  };
};

export const partnerIdSelector = (state: ApplicationState): string =>
  configKeySelector(state).partner_id;

export const selectedCaseStatusCodeSelector = (state: ApplicationState) => {
  const { data } = caseStatusStateSelector(state);
  return data?.length > 0
    ? (data[0].status.code as SynergyCaseStatusStepType)
    : -1;
};

export const followConfirmationStepsSelector = (state: ApplicationState) => {
  const enabledSteps = enableCaseStatusStepsSelector(state);
  return enabledSteps
    ? caseStatusSteps.filter(({ key: stepName }) =>
        enabledSteps.some(({ step }) => step === stepName),
      )
    : [];
};

export const caseStatusStepsSelector = (state: ApplicationState) => {
  const enabledSteps = enableCaseStatusStepsSelector(state);
  const shouldDisplayAllServiceActivityDetailsPerStep =
    shouldDisplayAllServiceActivityDetailsPerStepSelector(state);
  const enabledStepsFiltered = enabledSteps
    ? caseStatusSteps.filter(({ key: stepName }) =>
        enabledSteps.some(({ step }) => step === stepName),
      )
    : [];

  const selectedStatusCode = selectedCaseStatusCodeSelector(state);

  return shouldDisplayAllServiceActivityDetailsPerStep
    ? enabledStepsFiltered
    : enabledStepsFiltered.map(step =>
        step.key === selectedStatusCode
          ? step
          : { key: step.key, title: step.title, text: null },
      );
};

export const caseStatusCurrentStepSelector = (state: ApplicationState) => {
  const { data } = caseStatusStateSelector(state);
  const steps = caseStatusStepsSelector(state);
  return data?.length > 0
    ? getStepIndex(steps, data[0].status.code as SynergyCaseStatusStepType)
    : -1;
};

export const maxIcvcCallAttemptsSelector = (state: ApplicationState): number =>
  +configKeySelector(state).max_icvc_call_attempts;

export const mapToRenderInLocationSelector = (state: ApplicationState) =>
  configKeySelector(state).map_to_render_in_location_synergy;

export const mapToRenderInDestinationSelector = (state: ApplicationState) =>
  configKeySelector(state).map_to_render_in_destination_synergy;

export const calculateDistanceEngineSelector = (state: ApplicationState) =>
  configKeySelector(state).calculate_distance_engine &&
  Object.keys(calculateDistanceEngines).includes(
    configKeySelector(state).calculate_distance_engine,
  )
    ? configKeySelector(state).calculate_distance_engine
    : calculateDistanceEngines.DEFAULT;

export const lengthUnitSelector = (state: ApplicationState) =>
  configKeySelector(state).length_unit;

export const mapValidCountriesSelector = (state: ApplicationState) =>
  configSelector(state)?.map_valid_countries;

export const showPolicyQuestionSelector = (state: ApplicationState) =>
  configKeySelector(state)?.should_show_policy_holder_question;

export const shouldHideFirstAndLastNameInMotorContactDetailsSelector = (
  state: ApplicationState,
) =>
  configKeySelector(state)
    ?.should_hide_first_and_last_name_in_motor_contact_details;

export const shouldDisplayBookAppointmentSelector = (state: ApplicationState) =>
  configKeySelector(state)?.should_display_book_appointment;

export const bookAppointmentMinutesOptionsSelector = (
  state: ApplicationState,
): string[] =>
  configKeySelector(state)?.book_appointment_minutes_options?.map(
    ({ value }) => value,
  ) || [];

export const bookAppointmentFirstSlotSelector = (state: ApplicationState) =>
  configKeySelector(state).book_appointment_first_slot_selected || [];

export const bookAppointmentFirstAvailableTimeSelector = (
  state: ApplicationState,
) => {
  const firstSlot = bookAppointmentFirstSlotSelector(state);
  return firstSlot.reduce(
    (obj, { type, value }) => ({
      ...obj,
      [type]: value,
    }),
    {},
  );
};

export const bookAppointmentDaysToShowSelector = (state: ApplicationState) =>
  configKeySelector(state).book_appointment_days_to_show;

export const bookAppointmentDaysEnabledToSelector = (state: ApplicationState) =>
  configKeySelector(state).book_appointment_days_enabled_to;

export const showConfirmationInContactDetailsSelector = (
  state: ApplicationState,
) => {
  const shouldShow =
    configKeySelector(state).should_show_request_assistance_in_contact_details;
  const steps = stepOrderSelector(state);

  if (steps.includes(routes.SUMMARY)) {
    return false;
  }

  return shouldShow;
};

export const showConfirmationFollowUpPageSelector = (state: ApplicationState) =>
  configKeySelector(state).should_display_confirmation_follow_up_page;

export const confirmationFollowUpLoadingImageSelector = (
  state: ApplicationState,
) => configKeySelector(state).confirmation_follow_up_loading_image;

export const showConfirmationFollowUpMapSelector = (state: ApplicationState) =>
  configKeySelector(state).should_show_confirmation_follow_up_map;

export const shouldHideMobilityContinuationButtonOnConfirmationFollowUpPageSelector =
  (state: ApplicationState) =>
    configKeySelector(state)
      .should_hide_mobility_continuity_button_on_confirmation_follow_up_page;

export const diagnosisTreeContextSelector = (
  state: ApplicationState,
): ContextQuestion[] => configSelector(state).diagnosis_tree_context;

export const servicesTreeContextSelector = (
  state: ApplicationState,
): ContextQuestion[] => configSelector(state).services_tree_context;

export const shouldHideGarageOrientationInConfirmationFollowUpSelector = (
  state: ApplicationState,
) =>
  configKeySelector(state)
    .should_hide_garage_orientation_in_confirmation_follow_up_page;

export const shouldDisplayCaseServicesSelector = (state: ApplicationState) =>
  configKeySelector(state).should_display_case_services_in_case_status;

const poToolsSelector = (state: ApplicationState): PoTools =>
  configSelector(state).po_tools || {};

export const shouldDisplayEventSelector = (state: ApplicationState): boolean =>
  poToolsSelector(state).shouldDisplayEventSelector;

export const shouldDisplayTranslatorSelector = (
  state: ApplicationState,
): boolean => poToolsSelector(state).shouldDisplayToolsBarTranslatorOption;

export const shouldDisplayPrismicButtonSelector = (
  state: ApplicationState,
): boolean => poToolsSelector(state).shouldDisplayToolsBarPrismicButton;

export const shouldDisplayReportButtonSelector = (
  state: ApplicationState,
): boolean => poToolsSelector(state).shouldDisplayToolsBarReportButton;

export const bugReportingDistributionListSelector = (
  state: ApplicationState,
): string => poToolsSelector(state).bugReportingDistributionList;

export const shouldDisplayToolsBarSelector = (
  state: ApplicationState,
): boolean =>
  shouldDisplayTranslatorSelector(state) ||
  shouldDisplayReportButtonSelector(state);

export const policyNumberCleanRegexSelector = (state: ApplicationState) =>
  configKeySelector(state).policy_number_clean_regex;

export const policyNumberFinalFormatSelector = (state: ApplicationState) =>
  configKeySelector(state).policy_number_final_format;

export const mobilityContinuationConfirmationImagesSelector = (
  state: ApplicationState,
) => configKeySelector(state).mobility_continuation_confirmation_images || [];

export const mobilityContinuationCallbackImageSelector = (
  state: ApplicationState,
) => configKeySelector(state).mobility_continuation_callback_image;

export const shouldCreateMCServicesOnlyForFilteredSelector = (
  state: ApplicationState,
) => configKeySelector(state).should_create_filtered_services_only;

export const checkPartialDataInVehicleDetailsSelector = (
  state: ApplicationState,
) => configKeySelector(state).should_check_partial_data_in_vehicle_details;

export const showTrailerQuestionSelector = (state: ApplicationState) =>
  configKeySelector(state).should_show_trailer_question_in_vehicle_details;

export const customFuelTypesSelector = (state: ApplicationState): string[] =>
  configSelector(state).custom_fuel_types || [];

export const customVehicleTypesSelector = (state: ApplicationState): string[] =>
  configSelector(state).custom_vehicle_types || [];

export const customVehicleBrandSelector = (state: ApplicationState): string[] =>
  configSelector(state).custom_vehicle_brands || [];

export const shouldHideMobilityContButtonIfNoPoliciesExistSelector = (
  state: ApplicationState,
): boolean =>
  configKeySelector(state)
    .should_hide_mobility_cont_button_if_no_policies_exist;
